import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/LanguageContext';
import { beatIndexAt, useNarration, usePlaybackClock, useStoredFlag } from './engine/usePlayback';
import { markWatched, updateProgress, useGuideProgress } from './progress';
import { buildTimeline } from './timeline';

// The session ends after 10 minutes without input (AuthWrapper listens for
// scroll on document); a playing video counts as activity.
const KEEP_ALIVE_MS = 60000;
// A topic counts as watched once this share of it has played
const WATCHED_SHARE = 0.8;

const startTimeOf = (timeline, start) => {
  if (start?.beat) return timeline.beats.find((beat) => beat.id === start.beat)?.start ?? 0;
  if (start?.chapter) return timeline.chapters.find((chapter) => chapter.key === start.chapter)?.start ?? 0;
  return 0;
};

/**
 * Everything the guide player does: the clock, narration (one line per step;
 * the clock waits for it), jumping to topics and steps, keyboard shortcuts,
 * full screen, keeping the session alive and remembering progress.
 * `start`: { chapter } or { beat } to begin from.
 */
const useGuidePlayer = (start) => {
  const { t, language } = useTranslation();
  const timeline = useMemo(() => buildTimeline(t), [t]);
  const [initialTime] = useState(() => startTimeOf(timeline, start));
  const { time, playing, setPlaying, rate, setRate, seek, holdRef } = usePlaybackClock(timeline, initialTime);
  const narration = useNarration(language);
  const [voiceOn, setVoiceOn] = useStoredFlag('habcare.userGuide.voice', true);
  const [captionsOn, setCaptionsOn] = useStoredFlag('habcare.userGuide.captions', true);
  const [speakNonce, setSpeakNonce] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const { watched } = useGuideProgress();
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const spokenRef = useRef(-1);
  const beatIdRef = useRef(null);
  const timelineRef = useRef(timeline);

  const index = beatIndexAt(timeline.beats, time);
  const beat = timeline.beats[index];
  const chapterIndex = timeline.chapters.findIndex((chapter) => chapter.key === beat.chapter);
  const chapter = timeline.chapters[chapterIndex];
  const ended = time >= timeline.total;
  const narrate = voiceOn && narration.available;

  // Opened with a click, so the video (and its voice) may start right away.
  // Focus goes to the picture so Space pauses instead of pressing a button.
  useEffect(() => {
    setPlaying(true);
    const focusTimer = window.setTimeout(() => stageRef.current?.focus({ preventScroll: true }), 150);
    return () => window.clearTimeout(focusTimer);
  }, [setPlaying]);

  // A new language changes the durations: stay on the same step
  useEffect(() => {
    if (timelineRef.current === timeline) return;
    timelineRef.current = timeline;
    const same = timeline.beats.find((item) => item.id === beatIdRef.current);
    if (same) seek(same.start);
  }, [timeline, seek]);

  // One narration line per step; the clock waits at the step's end until it is spoken
  useEffect(() => {
    if (!playing || !narrate || spokenRef.current === index) return;
    spokenRef.current = index;
    holdRef.current = { index, since: 0 };
    narration.speak(beat.caption, rate, () => {
      if (holdRef.current.index === index) holdRef.current = { index: -1, since: 0 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, playing, narrate, speakNonce]);

  // Paused or muted: stop speaking; resuming repeats the current line
  useEffect(() => {
    if (playing && narrate) return;
    narration.cancel();
    holdRef.current = { index: -1, since: 0 };
    spokenRef.current = -1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, narrate]);

  // Remember the step to continue from; the end clears it
  useEffect(() => {
    beatIdRef.current = beat.id;
    updateProgress({ resume: ended ? null : beat.id });
  }, [beat.id, ended]);

  useEffect(() => {
    if (playing && time - chapter.start >= (chapter.end - chapter.start) * WATCHED_SHARE) markWatched(chapter.key);
  }, [playing, time, chapter]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => document.dispatchEvent(new Event('scroll')), KEEP_ALIVE_MS);
    return () => window.clearInterval(timer);
  }, [playing]);

  const jump = useCallback((value) => {
    narration.cancel();
    spokenRef.current = -1;
    seek(value);
    setSpeakNonce((nonce) => nonce + 1);
  }, [narration, seek]);

  const togglePlay = useCallback(() => {
    if (ended) {
      jump(0);
      setPlaying(true);
    } else {
      setPlaying((value) => !value);
    }
  }, [ended, jump, setPlaying]);

  const goToChapter = useCallback((target) => {
    const clamped = Math.max(0, Math.min(timeline.chapters.length - 1, target));
    jump(timeline.chapters[clamped].start);
    setPlaying(true);
  }, [jump, setPlaying, timeline]);

  const goToBeat = useCallback((target) => {
    const clamped = Math.max(0, Math.min(timeline.beats.length - 1, target));
    jump(timeline.beats[clamped].start);
    setPlaying(true);
  }, [jump, setPlaying, timeline]);

  // "Önceki": the start of this chapter first, then the one before
  const prevChapter = () => goToChapter(time - chapter.start > 3000 ? chapterIndex : chapterIndex - 1);
  const nextChapter = () => goToChapter(chapterIndex + 1);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else frameRef.current?.requestFullscreen?.().catch(() => {});
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === frameRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  // Space plays / pauses, arrows go to the previous / next step, F is full screen
  // (fields, buttons and the slider keep their own keys)
  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === ' ' && tag !== 'BUTTON') {
        event.preventDefault();
        togglePlay();
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        goToBeat(index + (event.key === 'ArrowRight' ? 1 : -1));
      } else if (event.key === 'f' || event.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, goToBeat, toggleFullscreen, index]);

  return {
    timeline, time, playing, rate, setRate, index, beat, chapter, chapterIndex, ended, watched,
    narration, voiceOn, setVoiceOn, captionsOn, setCaptionsOn, fullscreen, frameRef, stageRef,
    jump, togglePlay, goToChapter, goToBeat, prevChapter, nextChapter, toggleFullscreen, setPlaying,
  };
};

export default useGuidePlayer;
